<template>
  <div class="glassmorphism-bg">
    <v-main>
      <v-container fluid class="pa-6">
        <!-- Header Card -->
        <div class="glassmorphism-card mb-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h2 class="page-title">List of Values</h2>
            <div class="d-flex gap-3">
              <v-btn
                color="success"
                rounded
                elevation="0"
                class="glassmorphism-btn success-btn"
                :loading="isSaving"
                :disabled="isSaving"
                @click="save"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save
              </v-btn>
              <v-btn
                color="info"
                rounded
                elevation="0"
                class="glassmorphism-btn info-btn"
              >
                <download-excel
                  :title="title"
                  :data="value_list_excel"
                  :fields="excel_fields"
                  worksheet="Value_lists"
                  name="Value_list.xls"
                >
                  <v-icon left>mdi-download</v-icon>
                  Export to Excel
                </download-excel>
              </v-btn>
            </div>
          </div>

          <!-- Value Selection -->
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="value_id"
                :items="valueItems"
                label="Select Value"
                prepend-inner-icon="mdi-format-list-text"
                outlined
                rounded
                dense
                class="glassmorphism-input"
                @change="getData"
              ></v-select>
            </v-col>
          </v-row>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-12">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <p class="text-h6 mt-4 text--secondary">Loading value lists...</p>
        </div>

        <!-- Data Table Card -->
        <div v-else-if="value_id" class="glassmorphism-card">
          <div class="glassmorphism-table-container">
            <v-simple-table
              fixed-header
              :height="valueLists.length ? '400px' : 'auto'"
              class="glassmorphism-table elevation-0"
            >
              <template v-slot:default>
                <thead class="glassmorphism-table-header">
                  <tr>
                    <th class="text-subtitle-2 font-weight-bold text-left" style="width: 10%">Sr No</th>
                    <th class="text-subtitle-2 font-weight-bold text-left" style="width: 40%">Description</th>
                    <th class="text-subtitle-2 font-weight-bold text-left" style="width: 35%">Code</th>
                    <th class="text-subtitle-2 font-weight-bold text-right" style="width: 15%">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(valueList, i) in valueLists" :key="`valueList${i}`" class="glassmorphism-table-row">
                    <td class="text-body-2 text--primary">{{ i + 1 }}</td>
                    <td class="pa-2">
                      <v-text-field
                        v-model="valueList.description"
                        outlined
                        dense
                        rounded
                        label="Description"
                        class="glassmorphism-input mb-0"
                        :input="(valueList.description = valueList.description.toUpperCase())"
                        hide-details="auto"
                        :error-messages="valueList.description == '' ? 'Description field is required' : ''"
                      ></v-text-field>
                    </td>
                    <td class="pa-2">
                      <v-text-field
                        v-model="valueList.code"
                        outlined
                        dense
                        rounded
                        label="Code"
                        class="glassmorphism-input mb-0"
                        :input="(valueList.code = valueList.code.toUpperCase())"
                        hide-details="auto"
                        :error-messages="valueList.code == '' ? 'Code field is required' : ''"
                      ></v-text-field>
                    </td>
                    <td class="text-right">
                      <v-btn
                        icon
                        small
                        color="error"
                        class="glassmorphism-icon-btn"
                        @click="valueList.id ? delete_data(valueList.id) : deleteValueList(valueList, i)"
                      >
                        <v-icon size="18">mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-if="value_id">
                    <td colspan="4" class="pa-3">
                      <v-btn
                        outlined
                        block
                        color="primary"
                        rounded
                        class="glassmorphism-btn"
                        @click="addEmptyValueList()"
                      >
                        <v-icon left>mdi-plus</v-icon>
                        Add New Row
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="glassmorphism-card">
          <div class="text-center py-12">
            <v-icon size="64" color="grey lighten-1">mdi-format-list-text</v-icon>
            <p class="text-h6 mt-4 text--secondary">Select a Value</p>
            <p class="text-body-2 text--secondary">Choose a value from the dropdown above to manage its list items</p>
          </div>
        </div>
      </v-container>
    </v-main>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ValueListsPage",
  data() {
    return {
      value_id: "",
      valueItems: [],
      valueLists: [],
      excel_fields: {
        "Sr No": "sr_no",
        DESCRIPTION: "description",
        CODE: "code",
      },
      title: "",
      value_list_excel: [],
      isLoading: false,
      isSaving: false,
    };
  },

  mounted() {
    this.getMasters();
  },
  methods: {
    async getMasters() {
      let masters = await axios.get("masters?context=value_list");
      masters = masters.data;
      masters.values.forEach((value) => {
        this.valueItems.push({
          id: value.id,
          text: value.name,
          value: value.id,
        });
      });
    },
    async getData() {
      this.isLoading = true;
      let valueLists = await axios.get(`/values/${this.value_id}/value_lists`);
      this.valueLists = valueLists.data.data;
      this.count = valueLists.data.count;
      this.isLoading = false;
      this.exportToExcel();
    },
    exportToExcel() {
      let value_name = this.valueItems.find(
        (valueItem) => valueItem.id === this.value_id
      );
      this.title = "List of " + value_name.text;
      this.value_list_excel = [];
      let sr_no = 1;
      this.valueLists.forEach((valuelist) => {
        this.value_list_excel.push({
          sr_no: sr_no,
          description: valuelist.description,
          code: valuelist.code,
        });
        sr_no++;
      });
    },
    searchData() {
      this.getData();
    },
    addEmptyValueList() {
      this.valueLists.push({
        value_id: this.value_id,
        company_id: this.company.id,
        description: "",
        code: "",
        is_active: 1,
      });
    },
    async save() {
      if (this.valueLists.length > 0) {
        this.isSaving = true;
        let payload = {
          datas: this.valueLists,
        };

        let response = await axios.post(
          `/values/${this.value_id}/multiple_value_lists`,
          payload
        );
        this.valueLists = response.data.data;
        this.isSaving = false;
      }
    },
    deleteValueList(valueList, index) {
      // Check if both description and code are empty
      if (!valueList.description.trim() && !valueList.code.trim()) {
        // If both are empty, simply remove the row from the valueLists array
        this.valueLists.splice(index, 1);
      } else {
        // If at least one field is not empty, proceed with the API call
        this.delete_data(valueList.id, index);
      }
    },
    async delete_data(id) {
      let check = confirm("Are you sure you want to delete the data");
      if (check) {
        try {
          this.isLoading = true;
          await axios.delete(`/values/${this.value_id}/value_lists/${id}`);
          this.isLoading = false;
          this.getData();
        } catch (e) {
          this.isLoading = false;
        }
      }
    },
  },
};
</script>
